import { createLazyResource } from '@magicdoorinc/ui';
import { createMemo, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { useBankAccountsFilter } from '~/hooks/useBankAccountsFilter';
import { bankAccountRepository } from '~/repositories/bankAccountRepository';
import { filterBankAccounts } from '~/utils/bankAccountLinkStatus';
import type { ListViewArgs } from '@magicdoorinc/ui';
import type { BankAccountsFilterValue } from '~/hooks/useBankAccountsFilter';

export const [BankAccountsListProvider, useBankAccountsList] = createMagicDoorContext('BankAccountsList', () => {
  const [bankAccounts, { refetch: refetchBankAccounts, mutate: mutateBankAccounts }] = createLazyResource(() =>
    bankAccountRepository.getBankAccounts()
  );
  const [search, setSearch] = createSignal('');
  const { filterItems, handleFilterReset, filterValue, setFilterValue } = useBankAccountsFilter({
    defaultValue: { plaidLinkStatus: 'All', stripeLinkStatus: 'All', connectionStatus: 'All' },
  });

  const filteredBankAccounts = createMemo(() => {
    const accounts = bankAccounts();
    if (!accounts) return [];
    return filterBankAccounts(accounts, search(), filterValue());
  });

  const onFilterChange = (args: ListViewArgs<BankAccountsFilterValue>) => {
    setSearch(args.search ?? '');
    setFilterValue(args.filter);
  };

  const [updateConnections, setUpdateConnections] = createSignal<{
    bankAccountId: string;
  }>();

  const findBankAccountById = (bankAccountId?: string | null) => {
    if (!bankAccountId) return undefined;
    return bankAccounts()?.find((account) => account.id === bankAccountId);
  };

  const getAllowedChartOfAccountIds = (bankAccountId?: string | null) => findBankAccountById(bankAccountId)?.chartOfAccountIds ?? [];

  const updateBankAccountChartOfAccount = (payload: { deletedChartOfAccountId: string; replacementChartOfAccountId: string }) => {
    const currentBankAccount = bankAccounts()?.find((account) => account.chartOfAccountIds.includes(payload.deletedChartOfAccountId));
    if (currentBankAccount) {
      refetchBankAccounts();
    }
  };

  return {
    refetchBankAccounts,
    mutateBankAccounts,
    bankAccounts,
    filterItems,
    filterValue,
    handleFilterReset,
    onFilterChange,
    filteredBankAccounts,
    updateConnections,
    setUpdateConnections,
    findBankAccountById,
    getAllowedChartOfAccountIds,
    updateBankAccountChartOfAccount,
  };
});
